import { FC } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ErrorBadgeIcon, SelectAllIcon, SpinningLoader, Text } from '@frontend/design-system';
import { useValidatePassword } from '../../hooks/use-validate-password';

const PasswordCheckRow: FC<{ isLoading?: boolean; isValid?: boolean; isNeutral?: boolean; title: string }> = ({
  isLoading,
  isValid,
  isNeutral,
  title,
}) => {
  return (
    <li css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
      <Text size='medium' color={isNeutral ? 'light' : isValid ? 'success' : 'error'}>
        {title}
      </Text>
      {isLoading ? (
        <SpinningLoader size='xs' />
      ) : isNeutral ? (
        <></>
      ) : isValid ? (
        <SelectAllIcon color='success' size={20} />
      ) : (
        <ErrorBadgeIcon color='error' size={18} />
      )}
    </li>
  );
};

export const PasswordRequirements = ({
  passwordInputStarted,
  isLengthShort,
  hasFirstName,
  hasLastName,
  hasUserEmail,
  isSecure,
  isCheckingSecurePW,
  minLength,
}: ReturnType<typeof useValidatePassword>) => {
  const { t } = useTranslation('resetPassword');

  return (
    <>
      <section>
        <ul css={ulStyle}>
          <li
            css={css`
              font-size: ${theme.font.size.medium};
            `}
          >
            {t('Should not include a common phrase or dictionary word')}
          </li>
          <PasswordCheckRow
            isNeutral={!passwordInputStarted}
            isValid={!isLengthShort}
            title={t('{{count}} characters minimum', { count: minLength })}
          />
          <PasswordCheckRow
            isNeutral={!passwordInputStarted}
            isValid={isSecure}
            isLoading={isCheckingSecurePW}
            title={t('Secure Password')}
          />
          <li
            css={css`
              padding: 0;
              font-weight: ${theme.font.weight.bold};
              margin-top: ${theme.spacing(2)};
            `}
          >
            {t('Does not include:')}
          </li>
          <PasswordCheckRow isNeutral={!passwordInputStarted} isValid={!hasFirstName} title={t('First name')} />
          <PasswordCheckRow isNeutral={!passwordInputStarted} isValid={!hasLastName} title={t('Last name')} />
          <PasswordCheckRow isNeutral={!passwordInputStarted} isValid={!hasUserEmail} title={t('Username')} />
        </ul>
      </section>
    </>
  );
};

const ulStyle = css({
  listStyle: 'none',
  padding: 0,
  color: theme.colors.neutral60,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
});
